import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { actualizarUsuarioAdmin } from './api/perfil';
import Sidebar from './Sidebar';
import { toast } from 'react-toastify';
import { Users, Search, Save, ChevronDown } from 'lucide-react';
import { ROLES, ROLE_META } from './roles';

const functions = getFunctions();
const listarUsuariosFn = httpsCallable(functions, 'listarUsuarios');

const ROLE_OPTIONS = Object.entries(ROLE_META).map(([val, meta]) => ({
    val, ...meta,
}));

function GestionUsuarios() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState({});  // { uid: true/false }

    const cargar = async () => {
        setLoading(true);
        try {
            const r = await listarUsuariosFn();
            setUsuarios(r.data.usuarios || []);
        } catch (e) {
            console.error(e);
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const cambiarRol = async (uid, nuevoRol) => {
        setSaving(p => ({ ...p, [uid]: true }));
        try {
            await actualizarUsuarioAdmin({ uid, rol: nuevoRol });
            setUsuarios(p => p.map(u => u.uid === uid ? { ...u, rol: nuevoRol } : u));
            toast.success('Perfil actualizado');
        } catch (e) {
            console.error(e);
            toast.error('Error al actualizar perfil');
        } finally {
            setSaving(p => ({ ...p, [uid]: false }));
        }
    };

    const [creditosInput, setCreditosInput] = useState({}); // { uid: texto en edición }

    const guardarCreditos = async (uid) => {
        const valor = Number(creditosInput[uid]);
        if (!Number.isFinite(valor) || valor < 0) { toast.error('Ingresa un número válido.'); return; }
        setSaving(p => ({ ...p, [uid]: true }));
        try {
            await actualizarUsuarioAdmin({ uid, creditosIa: valor });
            setUsuarios(p => p.map(u => u.uid === uid ? { ...u, creditosIa: valor } : u));
            setCreditosInput(p => { const n = { ...p }; delete n[uid]; return n; });
            toast.success('Créditos actualizados');
        } catch (e) {
            console.error(e);
            toast.error('Error al actualizar créditos');
        } finally {
            setSaving(p => ({ ...p, [uid]: false }));
        }
    };

    const filtrados = usuarios.filter(u =>
        (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.nombre || u.displayName || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="layout">
            <Sidebar />
            <div style={{ flex: 1, background: '#f8fafc', minHeight: '100vh' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', padding: 28 }}>

                    {/* Título */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={18} />
                        </div>
                        <div>
                            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Gestión de usuarios</h1>
                            <p style={{ fontSize: 13, color: '#9ca3af', margin: '2px 0 0' }}>Asigna perfiles de acceso a cada usuario</p>
                        </div>
                    </div>

                    {/* Descripción de perfiles */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                        {ROLE_OPTIONS.map(({ val, label, color, bg, border }) => (
                            <div key={val} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', borderLeft: `3px solid ${color}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                                    <span style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                                        {label}
                                    </span>
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                                    {val === ROLES.BASICO && 'Acceso a Catálogo y Novedades de precio.'}
                                    {val === ROLES.INTERMEDIO && 'Todo lo básico + Usados, Comparador, Asesor IA y Mis proyectos.'}
                                    {val === ROLES.COMPLETO && 'Acceso completo incluyendo Mantenedor y Gestión de usuarios.'}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Card principal */}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>

                        {/* Header con búsqueda */}
                        <div style={{ padding: '12px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                <input
                                    type="text"
                                    placeholder="Buscar por email o nombre…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={{ width: '100%', padding: '7px 10px 7px 32px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#111827', background: '#fff', outline: 'none' }}
                                />
                            </div>
                            <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 'auto' }}>
                                {filtrados.length} usuario{filtrados.length !== 1 ? 's' : ''}
                            </span>
                            <button
                                onClick={cargar}
                                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
                            >
                                ↺ Recargar
                            </button>
                        </div>

                        {/* Tabla */}
                        {loading ? (
                            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                                Cargando usuarios…
                            </div>
                        ) : filtrados.length === 0 ? (
                            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                                No se encontraron usuarios.
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb' }}>
                                        {['Usuario', 'Email', 'Perfil actual', 'Cambiar perfil', 'Créditos IA', 'Últ. acceso'].map(h => (
                                            <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtrados.map(u => {
                                        const rolActual = u.rol || ROLES.BASICO;
                                        const meta = ROLE_META[rolActual] || ROLE_META.basico;
                                        return (
                                            <tr key={u.uid} style={{ borderBottom: '1px solid #f3f4f6' }}>

                                                {/* Nombre */}
                                                <td style={{ padding: '11px 16px', fontWeight: 500, color: '#111827' }}>
                                                    {u.nombre || u.displayName || '—'}
                                                </td>

                                                {/* Email */}
                                                <td style={{ padding: '11px 16px', color: '#6b7280' }}>
                                                    {u.email || '—'}
                                                </td>

                                                {/* Perfil actual — badge */}
                                                <td style={{ padding: '11px 16px' }}>
                                                    <span style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                                                        {meta.label}
                                                    </span>
                                                </td>

                                                {/* Selector de perfil */}
                                                <td style={{ padding: '11px 16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <select
                                                            defaultValue={rolActual}
                                                            disabled={saving[u.uid]}
                                                            onChange={e => cambiarRol(u.uid, e.target.value)}
                                                            style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, background: '#fff', color: '#374151', cursor: 'pointer', minWidth: 130 }}
                                                        >
                                                            {ROLE_OPTIONS.map(({ val, label }) => (
                                                                <option key={val} value={val}>{label}</option>
                                                            ))}
                                                        </select>
                                                        {saving[u.uid] && (
                                                            <span style={{ fontSize: 11, color: '#9ca3af' }}>Guardando…</span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Créditos IA — bolsa manual, ilimitada para completo/superadmin */}
                                                <td style={{ padding: '11px 16px' }}>
                                                    {(rolActual === ROLES.COMPLETO || rolActual === ROLES.SUPERADMIN) ? (
                                                        <span style={{ fontSize: 12, color: '#9ca3af' }}>∞ Ilimitado</span>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                placeholder={String(u.creditosIa ?? 0)}
                                                                value={creditosInput[u.uid] ?? ''}
                                                                onChange={e => setCreditosInput(p => ({ ...p, [u.uid]: e.target.value }))}
                                                                onKeyDown={e => e.key === 'Enter' && guardarCreditos(u.uid)}
                                                                disabled={saving[u.uid]}
                                                                style={{ width: 90, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, color: '#374151' }}
                                                            />
                                                            <button
                                                                onClick={() => guardarCreditos(u.uid)}
                                                                disabled={saving[u.uid] || creditosInput[u.uid] === undefined}
                                                                title="Guardar créditos"
                                                                style={{ padding: '5px 7px', border: '1px solid #d1d5db', borderRadius: 7, background: '#fff', cursor: 'pointer', color: '#374151', display: 'flex' }}
                                                            >
                                                                <Save size={12} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Último acceso */}
                                                <td style={{ padding: '11px 16px', color: '#9ca3af', fontSize: 12 }}>
                                                    {u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString('es-CL') : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Nota */}
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16, lineHeight: 1.6 }}>
                        Los cambios de perfil son inmediatos — el usuario verá el menú actualizado en su próxima carga de página.
                        Los usuarios nuevos que se registren recibirán el perfil <strong>Básico</strong> por defecto.
                    </p>

                </div>
            </div>
        </div>
    );
}

export default GestionUsuarios;